import { pointSegmentDistanceSquared } from "./collision";
import { distance, distanceSquared, vec, type Vec2 } from "./math";
import type { SnakeState } from "./types";

export interface SpawnSafety {
  readonly boundaryMargin: number;
  readonly headDistance: number;
  readonly bodyDistance: number;
}

export interface SpawnDangerDistance {
  readonly snakeId: string;
  readonly point: Vec2;
  readonly distance: number;
}

export interface SpawnPointInspection {
  readonly candidate: Vec2;
  readonly boundaryDistance: number;
  readonly required: SpawnSafety;
  readonly nearestDangerousHead: SpawnDangerDistance | null;
  readonly nearestDangerousBody: SpawnDangerDistance | null;
}

export const DEFAULT_SPAWN_SAFETY: SpawnSafety = Object.freeze({
  boundaryMargin: 240,
  headDistance: 220,
  bodyDistance: 100,
});

const isProtected = (snake: SnakeState): boolean =>
  (snake.protectionRemainingSeconds ?? 0) > 0;

const closestPointOnSegment = (point: Vec2, start: Vec2, end: Vec2): Vec2 => {
  const segmentX = end.x - start.x;
  const segmentY = end.y - start.y;
  const lengthSquared = segmentX * segmentX + segmentY * segmentY;
  if (lengthSquared <= Number.EPSILON) return { ...start };
  const amount = Math.max(
    0,
    Math.min(
      1,
      ((point.x - start.x) * segmentX + (point.y - start.y) * segmentY) /
        lengthSquared,
    ),
  );
  return {
    x: start.x + segmentX * amount,
    y: start.y + segmentY * amount,
  };
};

export const inspectSpawnPoint = (
  point: Vec2,
  snakes: readonly SnakeState[],
  arenaRadius: number,
  safety: SpawnSafety = DEFAULT_SPAWN_SAFETY,
): SpawnPointInspection => {
  let nearestDangerousHead: SpawnDangerDistance | null = null;
  let nearestDangerousBody: SpawnDangerDistance | null = null;
  for (const snake of snakes) {
    if (!snake.alive || isProtected(snake)) continue;
    const headDistance = distance(point, snake.position);
    if (!nearestDangerousHead || headDistance < nearestDangerousHead.distance) {
      nearestDangerousHead = {
        snakeId: snake.id,
        point: { ...snake.position },
        distance: headDistance,
      };
    }
    for (let index = 1; index < snake.trail.points.length; index += 1) {
      const start = snake.trail.points[index - 1];
      const end = snake.trail.points[index];
      if (!start || !end) continue;
      const closest = closestPointOnSegment(point, start, end);
      const bodyDistance = distance(point, closest);
      if (
        !nearestDangerousBody ||
        bodyDistance < nearestDangerousBody.distance
      ) {
        nearestDangerousBody = {
          snakeId: snake.id,
          point: closest,
          distance: bodyDistance,
        };
      }
    }
  }
  return {
    candidate: { ...point },
    boundaryDistance: Math.max(0, arenaRadius - distance(point, vec(0, 0))),
    required: safety,
    nearestDangerousHead,
    nearestDangerousBody,
  };
};

export const isSafeSpawnPoint = (
  point: Vec2,
  snakes: readonly SnakeState[],
  arenaRadius: number,
  safety: SpawnSafety = DEFAULT_SPAWN_SAFETY,
): boolean => {
  if (distance(point, vec(0, 0)) > arenaRadius - safety.boundaryMargin)
    return false;

  for (const snake of snakes) {
    if (!snake.alive || isProtected(snake)) continue;
    if (distanceSquared(point, snake.position) < safety.headDistance ** 2)
      return false;
    for (let index = 1; index < snake.trail.points.length; index += 1) {
      const start = snake.trail.points[index - 1];
      const end = snake.trail.points[index];
      if (
        start &&
        end &&
        pointSegmentDistanceSquared(point, start, end) <
          safety.bodyDistance ** 2
      ) {
        return false;
      }
    }
  }
  return true;
};

/**
 * Searches a deterministic low-discrepancy spiral. The injected random offset
 * changes the sequence per Match without making spawn safety non-replayable.
 */
export const findSafeSpawnPoint = (
  snakes: readonly SnakeState[],
  arenaRadius: number,
  randomOffset = 0,
  attempts = 96,
): Vec2 | null => {
  const maximumRadius = arenaRadius - DEFAULT_SPAWN_SAFETY.boundaryMargin;
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  for (let index = 0; index < attempts; index += 1) {
    const amount =
      index === 0 ? 0 : Math.sqrt(index / Math.max(1, attempts - 1));
    const radius = amount * maximumRadius;
    const angle = randomOffset + index * goldenAngle;
    const candidate = vec(Math.cos(angle) * radius, Math.sin(angle) * radius);
    if (isSafeSpawnPoint(candidate, snakes, arenaRadius)) return candidate;
  }
  return null;
};
